import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { toast } from 'react-toastify'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'
import RestoreRoundedIcon from '@mui/icons-material/RestoreRounded'
import WrongLocationRoundedIcon from '@mui/icons-material/WrongLocationRounded'
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded'
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import CloudSyncOutlinedIcon from '@mui/icons-material/CloudSyncOutlined'
import { RAG as RAG_LEVELS } from 'features/dashboard/charts/scoreColour'
import { formatDate, formatTime } from 'utils/date'
import InfiniteScrollSentinel from 'components/InfiniteScrollSentinel'
import PdfFullScreenViewer from 'components/PdfFullScreenViewer'
import ImageViewer from 'components/ImageViewer'
import DuplicatesPanel from 'features/bpDetails/Components/files/DuplicatesPanel'
import SharePointCleansePanel from 'features/bpDetails/Components/files/SharePointCleansePanel'
import ProvenanceChip from 'features/bpDetails/Components/files/ProvenanceChip'
import ReviewPanel from 'features/bpDetails/Components/files/ReviewPanel'
import ReviewActionsLegend from 'features/bpDetails/Components/files/ReviewActionsLegend'
import ReviewActionSlider from 'features/bpDetails/Components/files/ReviewActionSlider'
import RecommendedRemedialsPanel from 'features/bpDetails/Components/files/RecommendedRemedialsPanel'
import GuruInsightModal from 'features/bpDetails/Components/files/GuruInsightModal'
import {
  getFileAnalysisSummary,
  getCorruptedFiles,
  getRejectedUploads,
  getUploadBatches,
  getUploadBatchFiles,
  bulkDeleteBatchFiles,
  getWrongBuildingFiles,
  resolveWrongBuildingFlag,
  restoreBpFile,
  getFileUrls,
  setFileReviewAction,
  markFilesForDeletion
} from 'services/fileService'

const PER_PAGE = 25

const confidenceTone = c => (c >= 85 ? 'critical' : c >= 70 ? 'high' : 'medium')

const SECTIONS = [
  {
    key: 'wrong_building',
    label: 'Wrong building',
    Icon: WrongLocationRoundedIcon,
    tone: 'critical',
    explainer: 'Files whose address or UPRN doesn’t match this building. They may have been uploaded to the wrong place. Preview each one, then Keep it if it’s actually correct, or remove it. Files synced from SharePoint can’t be deleted here, so “Mark” adds them to the SharePoint cleanse list to remove at the source instead.',
    empty: { headline: 'All clear', sub: 'No files look like they belong to a different building.', tone: 'ok' },
    idKey: 'flagId',
    fileIdKey: 'fileId',
    searchable: false,
    actionsWidth: '250px',
    fetch: (bpId, { page }) => getWrongBuildingFiles(bpId, { page, perPage: PER_PAGE }),
    columns: [
      { header: 'File', width: '28%', render: r => <LinkName>{r.displayName}</LinkName> },
      { header: 'From', width: '14%', render: r => <ProvenanceChip provenance={r.provenance} /> },
      { header: 'Confidence', width: '13%', render: r => <Chip $tone={confidenceTone(r.confidence)}>{r.confidence}%</Chip> },
      { header: 'Why it was flagged', width: '31%', render: r => <Muted>{r.reasoning}</Muted> },
      { header: 'Flagged', width: '14%', render: r => <Muted>{formatDate(r.flaggedAt)}</Muted> }
    ]
  },
  {
    key: 'corrupted',
    label: 'Corrupted',
    Icon: ReportProblemRoundedIcon,
    tone: 'high',
    explainer: 'Files we couldn’t read automatically. They may be scanned images, password-protected, or an unusual format. Preview to check what each one is, then delete the ones that are no good. Files synced from SharePoint can’t be deleted here, so “Mark for deletion” adds them to the SharePoint cleanse list instead.',
    empty: { headline: 'Nothing unreadable', sub: 'Every file here was processed successfully.', tone: 'ok' },
    idKey: 'id',
    fileIdKey: 'id',
    searchable: true,
    actionsWidth: '250px',
    fetch: (bpId, { page, search }) => getCorruptedFiles(bpId, { page, perPage: PER_PAGE, search }),
    columns: [
      { header: 'File', width: '26%', render: r => <LinkName>{r.displayName}</LinkName> },
      { header: 'From', width: '14%', render: r => <ProvenanceChip provenance={r.provenance} /> },
      { header: 'Type', width: '15%', render: r => <Muted>{r.fileType || '-'}</Muted> },
      { header: 'What went wrong', width: '30%', render: r => <Muted>{r.reason}</Muted> },
      { header: 'Last tried', width: '15%', render: r => <Muted>{formatDate(r.lastAttempt)}</Muted> }
    ]
  },
  {
    key: 'duplicates',
    label: 'Duplicates',
    Icon: ContentCopyRoundedIcon,
    tone: 'none',
    embed: true,
    explainer: 'The same document can end up saved more than once. We group files that share a name and size so you can keep the best copy and clear the rest. The panel below explains what each number means.'
  },
  {
    key: 'sharepoint_cleanse',
    label: 'SharePoint cleanse',
    navLabel: 'SharePoint',
    Icon: CloudSyncOutlinedIcon,
    tone: 'none',
    explainer: 'SharePoint files marked for deletion. Files synced from SharePoint can’t be deleted here, so review this list, then remove them in SharePoint at the source (export it as a CSV to work through). Unmark anything you want to keep.'
  },
  {
    key: 'review',
    label: 'Review',
    Icon: TaskAltRoundedIcon,
    tone: 'none',
    explainer: 'Go through your files and decide what to do with each one: Keep, Hide, or Delete. Actioned files are listed below, where you can change your decision.'
  },
  {
    key: 'remedials',
    label: 'Recommended remedials',
    navLabel: 'Remedials',
    Icon: AutoAwesomeRoundedIcon,
    tone: 'none',
    explainer: 'AI-recommended remedials found across this building’s documents. Turn one into a task to get it done. Creating a task needs Task Management enabled.'
  },
  {
    key: 'rejected',
    label: 'Rejected uploads',
    navLabel: 'Rejected',
    Icon: BlockRoundedIcon,
    tone: 'medium',
    explainer: 'Files that were part of an upload but couldn’t be added, usually an unsupported type (.zip, .exe…) or a file that didn’t finish uploading. These were never saved.',
    empty: { headline: 'Nothing rejected', sub: 'Every file you uploaded was accepted.', tone: 'ok' },
    idKey: 'id',
    searchable: false,
    fetch: (bpId, { page, batch }) => getRejectedUploads(bpId, { page, perPage: PER_PAGE, batch }),
    columns: [
      { header: 'File', width: '30%', render: r => <Strong>{r.displayName}</Strong> },
      { header: 'From', width: '14%', render: r => <ProvenanceChip provenance={r.provenance} /> },
      { header: 'Why it was rejected', width: '34%', render: r => <Muted>{r.reason}</Muted> },
      { header: 'When', width: '22%', render: r => <Muted>{formatDate(r.rejectedAt)}</Muted> }
    ]
  },
  {
    key: 'batches',
    label: 'Upload/Delete history',
    navLabel: 'History',
    Icon: HistoryRoundedIcon,
    tone: 'none',
    explainer: 'A day-by-day record of documents added to this building, most recent first. Expand a day to see its files, where deleted ones are shown crossed out. You can bulk-delete a whole day or a selection; files synced from SharePoint are managed there and can’t be deleted here.',
    empty: { headline: 'No uploads yet', sub: 'Once documents are added, you’ll see a day-by-day history here.', tone: 'neutral' },
    idKey: 'id',
    searchable: false,
    fetch: (bpId, { page }) => getUploadBatches(bpId, { page, perPage: PER_PAGE }),
    columns: [
      { header: 'Date', width: '36%', render: r => <Strong>{formatDate(r.date)}</Strong> },
      { header: 'Documents added', width: '32%', render: r => Number(r.files).toLocaleString('en-GB') },
      { header: 'Added by', width: '32%', render: r => <Muted>{r.uploader}</Muted> }
    ]
  }
]

const AnalysisList = ({ bpId, section, canEdit, onCountChange, onOpenInsight }) => {
  const [items, setItems] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [busyId, setBusyId] = useState(null)
  const [previewFile, setPreviewFile] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewingId, setPreviewingId] = useState(null)
  const searchRef = useRef('')

  const load = useCallback((page) => {
    setLoading(true)
    section.fetch(bpId, { page, search: searchRef.current })
      .then(res => {
        const payload = res?.data?.data || res?.data || []
        const incoming = Array.isArray(payload) ? payload : (payload.data || [])
        const m = res?.data?.meta || null
        setMeta(m)
        setItems(prev => {
          if ((m?.currentPage ?? 1) > 1) {
            const ids = new Set(prev.map(x => x[section.idKey]))
            return [...prev, ...incoming.filter(x => !ids.has(x[section.idKey]))]
          }
          return incoming
        })
        if (m && onCountChange) onCountChange(section.key, m.total)
      })
      .catch(() => toast.error('Could not load this list. Please try again.'))
      .finally(() => { setLoading(false); setInitialLoading(false) })
  }, [bpId, section, onCountChange])

  useEffect(() => {
    searchRef.current = ''
    setSearch('')
    setInitialLoading(true)
    load(1)
  }, [bpId, section.key])

  const onSearch = e => {
    const v = e.target.value
    setSearch(v)
    searchRef.current = v
    setInitialLoading(true)
    load(1)
  }

  const removeRow = id => {
    setItems(prev => prev.filter(x => x[section.idKey] !== id))
    setMeta(m => (m ? { ...m, total: Math.max(0, (m.total || 1) - 1) } : m))
    if (onCountChange) onCountChange(section.key, Math.max(0, (meta?.total || 1) - 1))
  }

  const applyDecision = (row, action) => {
    const rowId = row[section.idKey]
    const fileId = row[section.fileIdKey] ?? row.id
    const isSharepoint = row.isSharepoint || row.provenance === 'sharepoint'
    if (busyId === rowId) return
    if (action === 'delete' && !window.confirm(
      isSharepoint
        ? `Mark “${row.displayName}” for deletion? SharePoint files can’t be deleted here, so this adds it to the SharePoint cleanse list to remove at the source.`
        : `Delete “${row.displayName}”? You can restore it afterwards from the Upload/Delete history.`
    )) return
    setBusyId(rowId)
    const primary = (action === 'delete' && isSharepoint)
      ? markFilesForDeletion(bpId, [fileId])
      : setFileReviewAction(bpId, fileId, action)
    const done = (section.key === 'wrong_building' && row.flagId)
      ? primary.then(() => resolveWrongBuildingFlag(bpId, row.flagId))
      : primary
    done
      .then(() => {
        toast.success(action === 'keep'
          ? 'Kept. No longer needs attention.'
          : action === 'hide'
            ? 'Hidden. It won’t show in lists or Guru.'
            : isSharepoint
              ? 'Marked for deletion. See the SharePoint cleanse tab.'
              : 'Removed from the Building Passport.')
        removeRow(rowId)
      })
      .catch(() => toast.error('Could not apply that action. Please try again.'))
      .finally(() => setBusyId(null))
  }

  const preview = row => {
    const fileId = row[section.fileIdKey] ?? row.id
    if (previewingId) return
    setPreviewingId(fileId)
    getFileUrls(bpId, fileId)
      .then(res => {
        const urls = res?.data?.data || res?.data || {}
        const fileUrl = urls.url || ''
        const fileName = row.displayName || ''
        if (/\.pdf/i.test(fileUrl) || /\.pdf/i.test(fileName) ||
            /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(fileUrl) || /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(fileName)) {
          setPreviewFile({ ...row, url: fileUrl, originalUrl: urls.originalUrl })
          setPreviewOpen(true)
        } else {
          window.open(urls.originalUrl || fileUrl, '_blank', 'noopener')
        }
      })
      .catch(() => toast.error('Could not open a preview for this file.'))
      .finally(() => setPreviewingId(null))
  }

  const renderActions = row => {
    if (!canEdit || !['wrong_building', 'corrupted'].includes(section.key)) return null
    const rowId = row[section.idKey]
    const fileId = row[section.fileIdKey] ?? row.id
    return (
      <RowBtns>
        <RowBtn disabled={previewingId === fileId} onClick={() => preview(row)}>
          <VisibilityOutlinedIcon style={{ fontSize: 15 }} /> {previewingId === fileId ? 'Opening…' : 'Preview'}
        </RowBtn>
        <ReviewActionSlider deleteLabel={(row.isSharepoint || row.provenance === 'sharepoint') ? 'Mark' : 'Delete'} disabled={busyId === rowId} onChange={a => applyDecision(row, a)} />
      </RowBtns>
    )
  }

  const hasActions = canEdit && ['wrong_building', 'corrupted'].includes(section.key)
  const actionsWidth = section.actionsWidth || '128px'
  const EmptyIcon = section.empty?.tone === 'ok' ? TaskAltRoundedIcon : section.Icon

  return (
    <>
    <div>
      {section.searchable && (
        <SearchInput value={search} onChange={onSearch} placeholder='Search by file name…' />
      )}
      {initialLoading ? (
        <Skeletons>{Array.from({ length: 6 }).map((_, i) => <SkelRow key={i} />)}</Skeletons>
      ) : items.length === 0 ? (
        <EmptyState>
          <EmptyIconWrap $tone={section.empty?.tone}><EmptyIcon style={{ fontSize: 30 }} /></EmptyIconWrap>
          <EmptyHeadline>{section.empty?.headline || 'Nothing here'}</EmptyHeadline>
          <EmptySub>{section.empty?.sub}</EmptySub>
        </EmptyState>
      ) : (
        <>
          <CountLine>Showing {items.length} of {meta?.total ?? items.length}</CountLine>
          {hasActions && <ReviewActionsLegend hasSharepoint={items.some(i => i.isSharepoint || i.provenance === 'sharepoint')} />}
          <Table>
            <THead>
              {section.columns.map(c => <TH key={c.header} style={{ width: c.width }}>{c.header}</TH>)}
              {hasActions && <TH style={{ flex: `0 0 ${actionsWidth}` }} />}
            </THead>
            {items.map(row => {
              const isCorrupted = section.key === 'corrupted'
              const openable = section.fileIdKey && row[section.fileIdKey]
              const clickable = isCorrupted ? !!row.id : !!openable
              const handleRowClick = isCorrupted
                ? () => preview(row)
                : (openable ? () => onOpenInsight({ id: row[section.fileIdKey], displayName: row.displayName }) : undefined)
              return (
                <TRow
                  key={row[section.idKey]}
                  $clickable={clickable}
                  onClick={handleRowClick}
                  title={isCorrupted ? 'Preview document' : (openable ? 'View document insight' : undefined)}
                >
                  {section.columns.map(c => <TD key={c.header} style={{ width: c.width }}>{c.render(row)}</TD>)}
                  {hasActions && <TD style={{ textAlign: 'right', flex: `0 0 ${actionsWidth}` }} onClick={e => e.stopPropagation()}>{renderActions(row)}</TD>}
                </TRow>
              )
            })}
          </Table>
          <InfiniteScrollSentinel
            hasMore={(meta?.currentPage ?? 1) < (meta?.lastPage ?? 1)}
            loading={loading}
            onLoadMore={() => load((meta?.currentPage ?? 1) + 1)}
          />
        </>
      )}
    </div>
    {previewFile && /\.pdf/i.test(previewFile.url || previewFile.displayName) && (
      <PdfFullScreenViewer
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewFile(null) }}
        file={previewFile}
      />
    )}
    {previewFile && /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(previewFile.url || previewFile.displayName) && (
      <ImageViewer
        open={previewOpen}
        onClose={() => { setPreviewOpen(false); setPreviewFile(null) }}
        file={previewFile}
      />
    )}
    </>
  )
}

const UploadHistoryList = ({ bpId, canEdit, emptyState, onCountChange, onOpenInsight }) => {
  const [days, setDays] = useState([])
  const [meta, setMeta] = useState(null)
  const [loading, setLoading] = useState(false)
  const [initialLoading, setInitialLoading] = useState(true)
  const [expanded, setExpanded] = useState({})
  const [filesByDay, setFilesByDay] = useState({})
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [deleting, setDeleting] = useState(false)
  const [previewFile, setPreviewFile] = useState(null)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [previewingId, setPreviewingId] = useState(null)
  const [restoringId, setRestoringId] = useState(null)

  const load = useCallback((page) => {
    setLoading(true)
    getUploadBatches(bpId, { page })
      .then(res => {
        const incoming = res?.data?.data || []
        const m = res?.data?.meta || null
        setMeta(m)
        setDays(prev => {
          if ((m?.currentPage ?? 1) > 1) {
            const ids = new Set(prev.map(x => x.id))
            return [...prev, ...incoming.filter(x => !ids.has(x.id))]
          }
          return incoming
        })
        if (m && onCountChange) onCountChange('batches', m.total)
      })
      .catch(() => toast.error('Could not load the upload history. Please try again.'))
      .finally(() => { setLoading(false); setInitialLoading(false) })
  }, [bpId, onCountChange])

  useEffect(() => { setInitialLoading(true); load(1) }, [bpId])

  const loadDayFiles = (date, page = 1) => {
    setFilesByDay(prev => ({
      ...prev,
      [date]: { ...(prev[date] || { items: [] }), loading: page === 1, loadingMore: page > 1 }
    }))
    return getUploadBatchFiles(bpId, { date, page, perPage: 100 })
      .then(res => {
        const incoming = res?.data?.data || []
        const m = res?.data?.meta || null
        setFilesByDay(prev => {
          const existing = page > 1 ? (prev[date]?.items || []) : []
          const items = [...existing, ...incoming.filter(i => !existing.some(e => e.id === i.id))]
          return { ...prev, [date]: { items, meta: m, loading: false, loadingMore: false } }
        })
        return m
      })
      .catch(() => {
        setFilesByDay(prev => ({ ...prev, [date]: { ...(prev[date] || { items: [] }), loading: false, loadingMore: false } }))
        toast.error('Could not load the files for this day.')
        return null
      })
  }


  const toggle = (date) => {
    setExpanded(prev => {
      const next = { ...prev, [date]: !prev[date] }
      if (next[date] && !filesByDay[date]) loadDayFiles(date)
      return next
    })
  }

  const isDeletable = f => !f.isDeleted && !f.isSharepoint
  const dayDeletable = (date) => (filesByDay[date]?.items || []).filter(isDeletable)

  const preview = f => {
    if (previewingId) return
    setPreviewingId(f.id)
    getFileUrls(bpId, f.id)
      .then(res => {
        const urls = res?.data?.data || res?.data || {}
        const fileUrl = urls.url || ''
        const fileName = f.displayName || ''
        if (/\.pdf/i.test(fileUrl) || /\.pdf/i.test(fileName) ||
            /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(fileUrl) || /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(fileName)) {
          setPreviewFile({ ...f, url: fileUrl, originalUrl: urls.originalUrl })
          setPreviewOpen(true)
        } else {
          window.open(urls.originalUrl || fileUrl, '_blank', 'noopener')
        }
      })
      .catch(() => toast.error('Could not open a preview for this file.'))
      .finally(() => setPreviewingId(null))
  }

  const restore = f => {
    if (restoringId) return
    setRestoringId(f.id)
    restoreBpFile(bpId, f.id)
      .then(() => {
        toast.success('File restored.')
        setFilesByDay(prev => {
          const next = {}
          Object.entries(prev).forEach(([d, v]) => {
            next[d] = { ...v, items: (v.items || []).map(x => (x.id === f.id ? { ...x, isDeleted: false } : x)) }
          })
          return next
        })
      })
      .catch(() => toast.error('Could not restore this file.'))
      .finally(() => setRestoringId(null))
  }

  const toggleSelect = (id) => setSelectedIds(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  const toggleSelectDay = (date) => {
    const d = dayDeletable(date)
    setSelectedIds(prev => {
      const next = new Set(prev)
      const all = d.length > 0 && d.every(f => next.has(f.id))
      d.forEach(f => (all ? next.delete(f.id) : next.add(f.id)))
      return next
    })
  }

  const allDaySelected = (date) => {
    const d = dayDeletable(date)
    return d.length > 0 && d.every(f => selectedIds.has(f.id))
  }

  const clearSelection = () => setSelectedIds(new Set())

  const resultToast = (r) => {
    const parts = [`${r?.deleted ?? 0} deleted`]
    if (r?.skippedSharepoint) parts.push(`${r.skippedSharepoint} SharePoint skipped`)
    toast.success(parts.join(' · '))
  }

  const markDeletedLocally = (ids) => {
    const idSet = new Set(ids)
    setFilesByDay(prev => {
      const next = {}
      Object.entries(prev).forEach(([d, v]) => {
        next[d] = { ...v, items: (v.items || []).map(f => (idSet.has(f.id) ? { ...f, isDeleted: true } : f)) }
      })
      return next
    })
    setSelectedIds(prev => {
      const next = new Set(prev)
      ids.forEach(id => next.delete(id))
      return next
    })
  }

  const deleteSelected = () => {
    const ids = [...selectedIds]
    if (!ids.length || deleting) return
    if (!window.confirm(`Delete ${ids.length} file${ids.length === 1 ? '' : 's'}? You can restore them afterwards from the Upload/Delete history.`)) return
    setDeleting(true)
    bulkDeleteBatchFiles(bpId, { fileIds: ids })
      .then(res => { markDeletedLocally(ids); resultToast(res?.data) })
      .catch(() => toast.error('Could not delete the selected files.'))
      .finally(() => setDeleting(false))
  }

  const deleteDay = (date) => {
    if (deleting) return
    if (!window.confirm(`Delete every deletable document uploaded on ${formatDate(date)}? This removes them all, including any not yet loaded below. You can restore them from this history afterwards. SharePoint files are left untouched.`)) return
    setDeleting(true)
    bulkDeleteBatchFiles(bpId, { date })
      .then(res => loadDayFiles(date, 1).then(() => resultToast(res?.data)))
      .catch(() => toast.error('Could not delete this day.'))
      .finally(() => setDeleting(false))
  }

  if (initialLoading) {
    return <Skeletons>{Array.from({ length: 6 }).map((_, i) => <SkelRow key={i} />)}</Skeletons>
  }

  if (days.length === 0) {
    return (
      <EmptyState>
        <EmptyIconWrap $tone={emptyState?.tone}><HistoryRoundedIcon style={{ fontSize: 30 }} /></EmptyIconWrap>
        <EmptyHeadline>{emptyState?.headline || 'No uploads yet'}</EmptyHeadline>
        <EmptySub>{emptyState?.sub}</EmptySub>
      </EmptyState>
    )
  }

  return (
    <>
      {canEdit && selectedIds.size > 0 && (
        <BulkBar>
          <BulkInfo>{selectedIds.size} file{selectedIds.size === 1 ? '' : 's'} selected</BulkInfo>
          <ClearBtn type='button' onClick={clearSelection} disabled={deleting}>Clear</ClearBtn>
          <BulkDeleteBtn type='button' onClick={deleteSelected} disabled={deleting}>
            <DeleteOutlineRoundedIcon style={{ fontSize: 16 }} /> Delete selected
          </BulkDeleteBtn>
        </BulkBar>
      )}
      <CountLine>Showing {days.length} of {meta?.total ?? days.length} day{(meta?.total ?? days.length) === 1 ? '' : 's'}</CountLine>
      <DayList>
        {days.map(day => {
          const isOpen = !!expanded[day.id]
          const dayFiles = filesByDay[day.id]
          const count = Number(day.files)
          const deletableCount = dayDeletable(day.id).length
          return (
            <DayGroup key={day.id}>
              <DayHeader type='button' $open={isOpen} onClick={() => toggle(day.id)} aria-expanded={isOpen}>
                <Chevron $open={isOpen}><KeyboardArrowRightRoundedIcon style={{ fontSize: 20 }} /></Chevron>
                <DayDate>{formatDate(day.date)}</DayDate>
                <DayMeta>{count.toLocaleString('en-GB')} document{count === 1 ? '' : 's'} · {day.uploader}</DayMeta>
              </DayHeader>
              {isOpen && (
                <DayBody>
                  {(!dayFiles || dayFiles.loading) ? (
                    <Skeletons>{Array.from({ length: 3 }).map((_, i) => <SkelRow key={i} />)}</Skeletons>
                  ) : dayFiles.items.length === 0 ? (
                    <DayEmpty>No saved documents for this day. Anything from this upload may have been rejected or unreadable (see the other tabs).</DayEmpty>
                  ) : (
                    <>
                      {canEdit && dayFiles.items.length > 0 && (
                        <DayToolbar>
                          {deletableCount > 0 ? (
                            <SelectGroup>
                              <SelectAll title='Selects the files loaded below. To remove the whole day at once, use “Delete this day”.'>
                                <CheckboxInput type='checkbox' checked={allDaySelected(day.id)} onChange={() => toggleSelectDay(day.id)} />
                                Select all loaded ({deletableCount})
                              </SelectAll>
                              {selectedIds.size > 0 && (
                                <DeselectAllBtn type='button' onClick={clearSelection} disabled={deleting} title='Clears your whole selection across every day'>
                                  Deselect all
                                </DeselectAllBtn>
                              )}
                            </SelectGroup>
                          ) : (
                            <DaySharePointHint>These files are synced from SharePoint, so remove them from the SharePoint cleanse tab.</DaySharePointHint>
                          )}
                          {deletableCount > 0 && (
                            <DeleteDayBtn type='button' onClick={() => deleteDay(day.id)} disabled={deleting}>
                              <DeleteOutlineRoundedIcon style={{ fontSize: 15 }} /> Delete this day
                            </DeleteDayBtn>
                          )}
                        </DayToolbar>
                      )}
                      {dayFiles.items.map(f => {
                        const selected = selectedIds.has(f.id)
                        return (
                          <FileRow key={f.id} $deleted={f.isDeleted} $selected={selected}>
                            {canEdit && (
                              <CheckSlot onClick={e => e.stopPropagation()}>
                                {isDeletable(f)
                                  ? <CheckboxInput type='checkbox' checked={selected} onChange={() => toggleSelect(f.id)} />
                                  : <CheckSpacer />}
                              </CheckSlot>
                            )}
                            <FileMain
                              $clickable={!f.isDeleted}
                              onClick={f.isDeleted ? undefined : () => preview(f)}
                              title={f.isDeleted ? undefined : (previewingId === f.id ? 'Opening…' : 'Preview document')}
                            >
                              <FileName $deleted={f.isDeleted}>{f.displayName}</FileName>
                              <FileTags>
                                {f.isDeleted && <DeletedChip>Deleted</DeletedChip>}
                                <ProvenanceChip provenance={f.provenance} />
                                {f.fileCategory && <TagChip $kind='cat'>{f.fileCategory}</TagChip>}
                                {f.fileType && <TagChip $kind='type'>{f.fileType}</TagChip>}
                                {f.customFolder && <FolderChip><FolderOutlinedIcon style={{ fontSize: 13 }} /> {f.customFolder}</FolderChip>}
                              </FileTags>
                            </FileMain>
                            {f.uploadedAt && <FileTime>{formatTime(f.uploadedAt)}</FileTime>}
                            {!f.isDeleted && (
                              <InsightCue $has={f.hasInsight} onClick={() => onOpenInsight({ id: f.id, displayName: f.displayName })}>
                                <AutoAwesomeRoundedIcon style={{ fontSize: 14 }} />
                                {f.hasInsight ? 'Insight' : 'Get insight'}
                              </InsightCue>
                            )}
                            {f.isDeleted && canEdit && (
                              <RestoreBtn disabled={restoringId === f.id} onClick={() => restore(f)}>
                                <RestoreRoundedIcon style={{ fontSize: 14 }} />
                                {restoringId === f.id ? 'Restoring…' : 'Restore'}
                              </RestoreBtn>
                            )}
                          </FileRow>
                        )
                      })}
                      {dayFiles.meta?.total > dayFiles.items.length && (
                        <DayCount>Showing {dayFiles.items.length} of {dayFiles.meta.total.toLocaleString('en-GB')} files, scroll for more</DayCount>
                      )}
                      <InfiniteScrollSentinel
                        hasMore={(dayFiles.meta?.currentPage ?? 1) < (dayFiles.meta?.lastPage ?? 1)}
                        loading={!!dayFiles.loadingMore}
                        onLoadMore={() => loadDayFiles(day.id, (dayFiles.meta?.currentPage ?? 1) + 1)}
                      />
                    </>
                  )}
                </DayBody>
              )}
            </DayGroup>
          )
        })}
      </DayList>
      <InfiniteScrollSentinel
        hasMore={(meta?.currentPage ?? 1) < (meta?.lastPage ?? 1)}
        loading={loading}
        onLoadMore={() => load((meta?.currentPage ?? 1) + 1)}
      />
      {previewFile && /\.pdf/i.test(previewFile.url || previewFile.displayName) && (
        <PdfFullScreenViewer
          open={previewOpen}
          onClose={() => { setPreviewOpen(false); setPreviewFile(null) }}
          file={previewFile}
        />
      )}
      {previewFile && /\.(jpg|jpeg|png|gif|svg|webp)$/i.test(previewFile.url || previewFile.displayName) && (
        <ImageViewer
          open={previewOpen}
          onClose={() => { setPreviewOpen(false); setPreviewFile(null) }}
          file={previewFile}
        />
      )}
    </>
  )
}

const FileAnalysisPanel = ({ bp, canEdit }) => {
  const [activeKey, setActiveKey] = useState('wrong_building')
  const [counts, setCounts] = useState({})
  const [insightFile, setInsightFile] = useState(null)

  useEffect(() => {
    getFileAnalysisSummary(bp.id)
      .then(res => {
        const c = res?.data?.data || {}
        setCounts({
          wrong_building: c.wrongBuilding ?? 0,
          corrupted: c.corrupted ?? 0,
          rejected: c.rejected ?? 0,
          batches: c.batches ?? 0,
          duplicates: c.duplicates ?? 0
        })
      })
      .catch(() => {})
  }, [bp.id])

  const onCountChange = useCallback((key, total) => {
    setCounts(prev => (prev[key] === total ? prev : { ...prev, [key]: total }))
  }, [])

  const active = useMemo(() => SECTIONS.find(s => s.key === activeKey), [activeKey])

  const selectSection = key => setActiveKey(key)

  const problemTone = s => (['wrong_building', 'corrupted', 'rejected'].includes(s.key) && counts[s.key] > 0 ? s.tone : 'none')

  return (
    <PanelContainer>
      <IntroRow>
        <IntroTitle>File analysis</IntroTitle>
        <IntroSub>Health and provenance of this building’s files. Problems first.</IntroSub>
      </IntroRow>

      <Nav>
        {SECTIONS.map(s => {
          const Icon = s.Icon
          const activeC = s.key === activeKey
          const tone = problemTone(s)
          return (
            <NavItem key={s.key} data-tour-id={s.key === 'review' ? 'files.nav-review' : undefined} $active={activeC} onClick={() => selectSection(s.key)}>
              <NavIcon><Icon style={{ fontSize: 18 }} /></NavIcon>
              <NavLabel>{s.navLabel || s.label}</NavLabel>
              {counts[s.key] !== undefined && (
                <NavCount $active={activeC} $tone={tone}>{counts[s.key]}</NavCount>
              )}
            </NavItem>
          )
        })}
      </Nav>

      <Body>
        <SectionHead>
          <HeadIcon $tone={active.tone}><active.Icon style={{ fontSize: 20 }} /></HeadIcon>
          <div>
            <HeadTitle>
              {active.label}
              {counts[active.key] !== undefined && <HeadCount>{counts[active.key]}</HeadCount>}
            </HeadTitle>
            {active.explainer && <HeadExplainer>{active.explainer}</HeadExplainer>}
          </div>
        </SectionHead>

        {active.key === 'sharepoint_cleanse' ? (
          <SharePointCleansePanel bp={bp} canEdit={canEdit} onCountChange={onCountChange} onOpenInsight={setInsightFile} />
        ) : active.key === 'review' ? (
          <ReviewPanel bp={bp} canEdit={canEdit} onOpenInsight={setInsightFile} />
        ) : active.key === 'remedials' ? (
          <RecommendedRemedialsPanel bp={bp} canEdit={canEdit} onCountChange={onCountChange} onOpenInsight={setInsightFile} />
        ) : active.embed ? (
          <DuplicatesPanel bp={bp} canEdit={canEdit} onOpenInsight={setInsightFile} />
        ) : active.key === 'batches' ? (
          <UploadHistoryList
            key='batches'
            bpId={bp.id}
            canEdit={canEdit}
            emptyState={active.empty}
            onCountChange={onCountChange}
            onOpenInsight={setInsightFile}
          />
        ) : (
          <AnalysisList
            key={active.key}
            bpId={bp.id}
            section={active}
            canEdit={canEdit}
            onCountChange={onCountChange}
            onOpenInsight={setInsightFile}
          />
        )}
      </Body>

      {insightFile && (
        <GuruInsightModal
          open={!!insightFile}
          onClose={() => setInsightFile(null)}
          file={insightFile}
          bpId={bp.id}
          buildingPassportName={bp.name}
        />
      )}
    </PanelContainer>
  )
}

const tint = tone => (RAG_LEVELS[tone] || RAG_LEVELS.none)

const PanelContainer = styled.div`padding: 6px 2px 2px;`
const IntroRow = styled.div`margin-bottom: 16px;`
const IntroTitle = styled.h2`font-size: 19px; font-weight: 800; color: #1b2233; margin: 0 0 3px;`
const IntroSub = styled.p`font-size: 13.5px; color: #6b7280; margin: 0;`

const Nav = styled.div`
  display: flex; flex-wrap: nowrap; gap: 4px; padding: 5px; margin-bottom: 18px; overflow-x: auto;
  background: #f5f6f9; border: 1px solid #ebedf1; border-radius: 12px;
  scrollbar-width: thin;
`
const NavItem = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 6px; flex: 1 1 auto; min-width: 0; justify-content: center;
  padding: 8px 10px; min-height: 40px; border-radius: 9px; cursor: pointer;
  font-size: 13.5px; font-weight: 700;
  color: ${({ $active }) => ($active ? '#1b2233' : '#5b6472')};
  background: ${({ $active }) => ($active ? '#fff' : 'transparent')};
  border: 1px solid ${({ $active }) => ($active ? '#e2e5ea' : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(20,30,60,0.06)' : 'none')};
  transition: background 140ms ease, color 140ms ease;
  &:hover { color: #1b2233; }
`
const NavLabel = styled.span`white-space: nowrap; overflow: hidden; text-overflow: ellipsis;`
const NavIcon = styled.span`flex: 0 0 auto; display: inline-flex;`
const NavCount = styled.span`
  flex: 0 0 auto; font-size: 12px; font-weight: 800; padding: 1px 7px; border-radius: 999px; min-width: 20px; text-align: center;
  color: ${({ $tone }) => ($tone === 'none' ? '#5b6472' : tint($tone).fg)};
  background: ${({ $tone }) => ($tone === 'none' ? '#eceef2' : tint($tone).bg)};
`

const Body = styled.div`min-height: 200px;`
const SectionHead = styled.div`display: flex; align-items: flex-start; gap: 14px; margin-bottom: 18px;`
const HeadIcon = styled.div`
  flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px; border-radius: 10px;
  color: ${({ $tone }) => tint($tone).fg}; background: ${({ $tone }) => tint($tone).bg};
`
const HeadTitle = styled.div`display: flex; align-items: center; gap: 10px; font-size: 16px; font-weight: 800; color: #1b2233;`
const HeadCount = styled.span`font-size: 12.5px; font-weight: 800; color: #6b7280; background: #f0f1f5; padding: 1px 9px; border-radius: 999px;`
const HeadExplainer = styled.p`font-size: 13px; line-height: 1.55; color: #6b7280; margin: 4px 0 0; max-width: 720px;`

const SearchInput = styled.input`
  font: inherit; width: 100%; max-width: 340px; margin-bottom: 14px;
  padding: 10px 13px; min-height: 42px; border-radius: 9px; border: 1px solid #e0e3e9; font-size: 14px;
  &:focus { outline: none; border-color: #1b2233; box-shadow: 0 0 0 3px rgba(27,34,51,0.06); }
`
const CountLine = styled.div`font-size: 12.5px; color: #8a90a0; margin-bottom: 8px;`
const Table = styled.div`width: 100%; overflow-x: auto; border: 1px solid #eef0f3; border-radius: 12px;`
const THead = styled.div`display: flex; padding: 12px 16px; background: #fafbfc; border-bottom: 1px solid #eef0f3;`
const TH = styled.div`font-size: 11px; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; color: #98a0af;`
const LinkName = styled.span`font-weight: 600; color: #33becc; word-break: break-word;`
const TRow = styled.div`
  display: flex; align-items: center; padding: 13px 16px; border-bottom: 1px solid #f3f4f7; font-size: 14px; color: #2b3242;
  transition: background 120ms ease;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  &:last-child { border-bottom: none; }
  &:hover { background: ${({ $clickable }) => ($clickable ? '#f5f8ff' : '#fafbfc')}; }
  &:hover ${LinkName} { text-decoration: underline; }
`
const TD = styled.div`padding-right: 12px; word-break: break-word;`
const Strong = styled.span`font-weight: 600; color: #1b2233;`
const Muted = styled.span`color: #6b7280; font-size: 13px;`
const Chip = styled.span`
  display: inline-block; font-size: 11.5px; font-weight: 800; padding: 2px 10px; border-radius: 999px;
  color: ${({ $tone }) => tint($tone).fg}; background: ${({ $tone }) => tint($tone).bg};
`
const RowBtns = styled.div`display: inline-flex; gap: 6px;`
const RowBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 4px; white-space: nowrap;
  padding: 6px 11px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer;
  color: ${({ $tone }) => ($tone === 'bad' ? RAG_LEVELS.critical.fg : $tone === 'ok' ? RAG_LEVELS.low.fg : '#4a4f5e')};
  background: ${({ $tone }) => ($tone === 'bad' ? RAG_LEVELS.critical.bg : $tone === 'ok' ? RAG_LEVELS.low.bg : '#f1f3f7')};
  border: 1px solid ${({ $tone }) => ($tone === 'bad' ? `${RAG_LEVELS.critical.fg}2e` : $tone === 'ok' ? `${RAG_LEVELS.low.fg}2e` : '#e6e8ec')};
  transition: filter 140ms ease;
  &:hover:not(:disabled) { filter: brightness(0.97); }
  &:disabled { opacity: 0.5; cursor: wait; }
`
const EmptyState = styled.div`display: flex; flex-direction: column; align-items: center; text-align: center; padding: 52px 16px;`
const EmptyIconWrap = styled.div`
  display: inline-flex; align-items: center; justify-content: center; width: 60px; height: 60px; border-radius: 16px; margin-bottom: 14px;
  color: ${({ $tone }) => ($tone === 'ok' ? RAG_LEVELS.low.fg : '#94a0b0')};
  background: ${({ $tone }) => ($tone === 'ok' ? RAG_LEVELS.low.bg : '#f1f3f7')};
`
const EmptyHeadline = styled.div`font-size: 16px; font-weight: 800; color: #1b2233; margin-bottom: 4px;`
const EmptySub = styled.div`font-size: 13.5px; color: #7a8194; max-width: 380px; line-height: 1.5;`
const Skeletons = styled.div`display: flex; flex-direction: column; gap: 10px; padding-top: 4px;`
const SkelRow = styled.div`
  height: 48px; border-radius: 10px; background: #eef0f3;
  @keyframes shimmer { 0% { opacity: 0.55; } 50% { opacity: 1; } 100% { opacity: 0.55; } }
  animation: shimmer 1.4s ease-in-out infinite;
`
const DayList = styled.div`display: flex; flex-direction: column; gap: 10px;`
const DayGroup = styled.div`
  border: 1px solid #eef0f3; border-radius: 12px; overflow: hidden; background: #fff;
`
const DayHeader = styled.button`
  font: inherit; width: 100%; display: flex; align-items: center; gap: 12px; text-align: left;
  padding: 14px 16px; cursor: pointer; background: ${({ $open }) => ($open ? '#fafbfc' : '#fff')};
  border: none; border-bottom: 1px solid ${({ $open }) => ($open ? '#eef0f3' : 'transparent')};
  transition: background 120ms ease;
  &:hover { background: #fafbfc; }
`
const Chevron = styled.span`
  flex: 0 0 auto; display: inline-flex; color: #98a0af;
  transform: rotate(${({ $open }) => ($open ? '90deg' : '0deg')});
  transition: transform 160ms ease;
`
const DayDate = styled.span`font-size: 14.5px; font-weight: 700; color: #1b2233; flex: 0 0 auto;`
const DayMeta = styled.span`font-size: 13px; color: #6b7280; margin-left: auto; text-align: right; word-break: break-word;`
const DayBody = styled.div`padding: 6px 10px 10px;`
const DayEmpty = styled.div`font-size: 13px; color: #7a8194; padding: 12px 8px; line-height: 1.5;`
const DayCount = styled.div`font-size: 12px; color: #8a90a0; padding: 8px 12px 4px; text-align: center;`
const DaySharePointHint = styled.span`
  font-size: 12.5px;
  color: #64748b;
`

const RestoreBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  border-radius: 7px;
  border: 1px solid #cbd5e1;
  background: #fff;
  color: #334155;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background-color 140ms ease;
  &:hover:not(:disabled) { background: #f1f5f9; }
  &:disabled { opacity: 0.5; cursor: wait; }
`

const FileRow = styled.div`
  display: flex; align-items: center; gap: 12px; padding: 11px 12px; border-radius: 9px;
  transition: background 120ms ease;
  background: ${({ $selected }) => ($selected ? '#eef4ff' : 'transparent')};
  opacity: ${({ $deleted }) => ($deleted ? 0.62 : 1)};
  &:hover { background: ${({ $selected }) => ($selected ? '#e4edff' : '#f7f8fa')}; }
`
const FileMain = styled.div`
  display: flex; flex-direction: column; gap: 6px; min-width: 0; flex: 1;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`
const FileName = styled.span`
  font-size: 14px; font-weight: 600; word-break: break-word;
  color: ${({ $deleted }) => ($deleted ? '#8a90a0' : '#33becc')};
  text-decoration: ${({ $deleted }) => ($deleted ? 'line-through' : 'none')};
  ${FileMain}:hover & { text-decoration: ${({ $deleted }) => ($deleted ? 'line-through' : 'underline')}; }
`
const FileTags = styled.div`display: flex; flex-wrap: wrap; gap: 6px; align-items: center;`
const TagChip = styled.span`
  display: inline-flex; font-size: 11.5px; font-weight: 700; padding: 2px 9px; border-radius: 999px; white-space: nowrap;
  color: ${({ $kind }) => ($kind === 'cat' ? '#3730a3' : '#334155')};
  background: ${({ $kind }) => ($kind === 'cat' ? '#eef2ff' : '#f1f3f7')};
`
const FolderChip = styled.span`
  display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 600; color: #6b7280;
  padding: 2px 9px 2px 7px; border-radius: 999px; background: #f5f6f9; border: 1px solid #ebedf1; white-space: nowrap;
`
const DeletedChip = styled.span`
  display: inline-flex; align-items: center; font-size: 11.5px; font-weight: 800; padding: 2px 9px; border-radius: 999px; white-space: nowrap;
  color: #b91c1c; background: #fee2e2;
`
const SharePointChip = styled.span`
  display: inline-flex; align-items: center; gap: 4px; font-size: 11.5px; font-weight: 700; padding: 2px 9px 2px 7px; border-radius: 999px; white-space: nowrap;
  color: #0369a1; background: #e0f2fe; border: 1px solid #bae6fd; cursor: help;
`
const FileTime = styled.span`
  flex: 0 0 auto; font-size: 12.5px; font-weight: 600; color: #8a90a0; white-space: nowrap; font-variant-numeric: tabular-nums;
`
const InsightCue = styled.span`
  flex: 0 0 auto; display: inline-flex; align-items: center; gap: 4px; font-size: 12px; font-weight: 700; cursor: pointer;
  padding: 5px 10px; border-radius: 8px; white-space: nowrap;
  color: ${({ $has }) => ($has ? '#4338ca' : '#5b6472')};
  background: ${({ $has }) => ($has ? '#eef2ff' : '#f1f3f7')};
  &:hover { filter: brightness(0.97); }
`
const CheckSlot = styled.div`flex: 0 0 auto; display: inline-flex; align-items: center; justify-content: center; width: 20px;`
const CheckSpacer = styled.span`display: inline-block; width: 16px;`
const CheckboxInput = styled.input`width: 16px; height: 16px; cursor: pointer; accent-color: #33becc; margin: 0;`
const DayToolbar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
  padding: 6px 12px 10px; margin-bottom: 2px; border-bottom: 1px dashed #eef0f3;
`
const SelectGroup = styled.div`
  display: inline-flex; align-items: center; gap: 12px; flex-wrap: wrap;
`
const SelectAll = styled.label`
  display: inline-flex; align-items: center; gap: 8px; font-size: 13px; font-weight: 600; color: #4a4f5e; cursor: pointer;
`
const DeselectAllBtn = styled.button`
  font: inherit; padding: 5px 11px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer;
  color: #4a4f5e; background: #f1f3f7; border: 1px solid #e6e8ec;
  transition: background 140ms ease;
  &:hover:not(:disabled) { background: #e9ecf1; }
  &:disabled { opacity: 0.5; cursor: wait; }
`
const DeleteDayBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
  padding: 6px 11px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer;
  color: ${RAG_LEVELS.critical.fg}; background: ${RAG_LEVELS.critical.bg}; border: 1px solid ${RAG_LEVELS.critical.fg}2e;
  transition: filter 140ms ease;
  &:hover:not(:disabled) { filter: brightness(0.97); }
  &:disabled { opacity: 0.5; cursor: wait; }
`
const BulkBar = styled.div`
  position: sticky; top: 0; z-index: 2; display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
  padding: 10px 14px; margin-bottom: 12px; border-radius: 10px; background: #1b2233; color: #fff;
`
const BulkInfo = styled.span`font-size: 13.5px; font-weight: 700; margin-right: auto;`
const ClearBtn = styled.button`
  font: inherit; padding: 6px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 700; cursor: pointer;
  color: #e5e7eb; background: transparent; border: 1px solid #3b4256;
  &:hover:not(:disabled) { background: #2a3145; }
  &:disabled { opacity: 0.5; cursor: wait; }
`
const BulkDeleteBtn = styled.button`
  font: inherit; display: inline-flex; align-items: center; gap: 5px; white-space: nowrap;
  padding: 6px 12px; border-radius: 8px; font-size: 12.5px; font-weight: 800; cursor: pointer;
  color: #fff; background: ${RAG_LEVELS.critical.fg}; border: 1px solid ${RAG_LEVELS.critical.fg};
  transition: filter 140ms ease;
  &:hover:not(:disabled) { filter: brightness(1.08); }
  &:disabled { opacity: 0.5; cursor: wait; }
`

export default FileAnalysisPanel
